import React, { useState, useMemo } from "react";
import { MdAddBox } from "react-icons/md";
import { IoIosRefreshCircle } from "react-icons/io";
import { useDispatch, useSelector } from "react-redux";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  getPaginationRowModel,
} from "@tanstack/react-table";
import { SlOptionsVertical } from "react-icons/sl";
import { getQuestionList, getSuppliersList } from "actions/Catalogs";
import { notify } from "utils";
import Loader from "components/UI/Loader";
import { useTranslation } from "react-i18next";
import ModalSuppliers from "components/Catalogs/Suppliers/ModalSuppliers";

const columnHelper = createColumnHelper();
const statusMap = {
  true: "Activo",
  false: "Inactivo",
};

const getColumns = (onViewDetails, t) => [
  columnHelper.accessor("id", { header: t("suppliers.id") }),
  columnHelper.accessor("name", { header: t("suppliers.name") }),
  columnHelper.accessor("nameCompany", { header: t("suppliers.companyName") }),
  columnHelper.accessor("status", {
    header: t("suppliers.status"),
    cell: ({ getValue }) => (
      <button
        className={` ${
          getValue() === true ? "bg-teal-200" : "bg-[#fdca3b]"
        } text-black text-xs font-bold rounded-full px-4 py-1 shadow-inner border`}
      >
        {statusMap[getValue()]}
      </button>
    ),
  }),
  columnHelper.display({
    id: "actions",
    cell: ({ row }) => (
      <div onClick={() => onViewDetails(row)} className="flex justify-center">
        <SlOptionsVertical className="rotate-180 text-xl cursor-pointer hover:text-yellow-800" />
      </div>
    ),
  }),
];

const Suppliers = () => {
  const [t] = useTranslation("catalog");
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [filterText, setFilterText] = useState("");
  const { currentProject } = useSelector((state) => state.auth);
  const [isLoading, setIsLoading] = useState(false);
  const [suppliersList, setSuppliersList] = useState([]);
  const [selected, setSelected] = useState(null);

  //Funcion para obtener las preguntas
  React.useEffect(() => {
    if (currentProject && currentProject?.id) {
      getData(currentProject.id);
    }
  }, [dispatch, currentProject]);

  const getData = (projectId) => {
    getSuppliersList(projectId).then((response) => {
      setSuppliersList(response || []);
    });
  };

  const onViewDetails = ({ original }) => {
    setSelected(original);
    setIsOpen(true);
  };

  const onRefresh = async () => {
    try {
      setIsLoading(true);
      await dispatch(getQuestionList());
    } catch (error) {
      console.error("Error al guardar la pregunta:", error);
      notify("error", "Hubo un problema al guardar", 2000);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredQuestions = useMemo(() => {
    if (!filterText) return suppliersList || [];
    return (suppliersList || []).filter((q) =>
      (q.name || q.description || "")
        .toLowerCase()
        .includes(filterText.toLowerCase())
    );
  }, [suppliersList, filterText]);

  const table = useReactTable({
    data: filteredQuestions || [],
    columns: getColumns(onViewDetails, t),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
  });

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-screen">
      <div className="bg-[#000000] rounded-t-lg h-[59px] w-full flex items-center justify-between px-4">
        <text className="text-[#ffffff] text-[16px] font-[Gravity] font-bold">
          {t("suppliers.title")}
        </text>
        <div className="flex gap-2 text-white text-4xl">
          <MdAddBox
            className="cursor-pointer"
            onClick={() => setIsOpen(true)}
          />
          <IoIosRefreshCircle className="cursor-pointer" onClick={onRefresh} />
        </div>
      </div>
      <div
        className={`flex flex-row items-start w-full h-3/4 overflow-x-scroll overflow-y-scroll shadow-scroll-x`}
      >
        <div className="w-full bg-white px-6 pt-4 pb-6 rounded-b-xl shadow-xl">
          <div className="overflow-x-auto">
            <div className="w-full flex justify-start mt-4">
              <input
                type="text"
                placeholder={t("suppliers.search")}
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="border border-gray-800 rounded px-3 py-2 w-72"
              />
            </div>
            <table className="min-w-full text-sm">
              <thead className="bg-white border-b-2 border-gray-400">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="px-4 py-5 text-left font-semibold text-black whitespace-nowrap"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b-2 border-gray-300 hover:bg-gray-50"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-4 py-2 whitespace-nowrap">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Paginación con estilo personalizado */}
          <div className="flex flex-row items-center justify-center mt-4">
            <button
              className="w-[20px] h-[20px]"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <img
                src={`${process.env.PUBLIC_URL}/icons/btns/btn_arrow_prev.svg`}
                width={50}
                alt={t("suppliers.previous")}
              />
            </button>

            {table.getCanPreviousPage() && (
              <button
                className="m-2 px-2 py-1 cursor-pointer"
                onClick={() =>
                  table.setPageIndex(table.getState().pagination.pageIndex - 1)
                }
              >
                <span className="font-bold font-[Gravity] text-[12px]">
                  {table.getState().pagination.pageIndex}
                </span>
              </button>
            )}

            <button
              className="bg-[#BCBEC1] m-2 rounded px-2 py-1 cursor-pointer"
              onClick={() =>
                table.setPageIndex(table.getState().pagination.pageIndex)
              }
            >
              <span className="font-bold font-[Gravity] text-[12px]">
                {table.getState().pagination.pageIndex + 1}
              </span>
            </button>

            {table.getCanNextPage() && (
              <button
                className="m-2 px-2 py-1 cursor-pointer"
                onClick={() =>
                  table.setPageIndex(table.getState().pagination.pageIndex + 1)
                }
              >
                <span className="font-bold font-[Gravity] text-[12px]">
                  {table.getState().pagination.pageIndex + 2}
                </span>
              </button>
            )}

            <button
              className="w-[20px] h-[20px]"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <img
                src={`${process.env.PUBLIC_URL}/icons/btns/btn_arrow_next.svg`}
                width={50}
                alt={t("suppliers.next")}
              />
            </button>
          </div>
        </div>
      </div>
      <ModalSuppliers
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          setSelected(null);
        }}
        selected={selected}
        getData={getData}
      />
    </div>
  );
};

export default Suppliers;
